import { useMemo, useEffect } from 'react';
import { createTheme, colors } from '@material-ui/core';
import shadows from '../theme/shadows';
import typography from '../theme/typography';
import { getBrandConfig } from '../config/brands';

const createCustomTheme = (themeConstants) => {
  const {
    PRIMARY_COLOR,
    PRIMARY_DARK,
    SECONDARY_COLOR,
    PRIMARY_TEXT,
    DARK_BACKGROUND,
    SUCCESS,
    WARNING,
    ERROR,
    BRANDING_MAIN,
    BRANDING_SHADE,
    TOPBAR,
    ACCENT_TEXT,
    UNIT_TOGGLE_BUTTON,
    MAIN_LAYOUT,
  } = themeConstants;

  return createTheme({
    palette: {
      background: {
        default: '#F3F6F8',
        paper: colors.common.white,
        dark: DARK_BACKGROUND,
      },
      branding: {
        main: BRANDING_MAIN,
        shade: BRANDING_SHADE,
      },
      topbar: {
        active: {
          background: TOPBAR.ACTIVE.BACKGROUND,
          color: TOPBAR.ACTIVE.COLOR,
        },
        inactive: {
          background: TOPBAR.INACTIVE.BACKGROUND,
          color: TOPBAR.INACTIVE.COLOR,
        },
        text: TOPBAR.TEXT,
      },
      toggleButton: {
        active: {
          background: UNIT_TOGGLE_BUTTON.ACTIVE.BACKGROUND,
          color: UNIT_TOGGLE_BUTTON.ACTIVE.COLOR,
          hover: {
            background: UNIT_TOGGLE_BUTTON.ACTIVE.HOVER.BACKGROUND,
            color: UNIT_TOGGLE_BUTTON.ACTIVE.HOVER.COLOR,
          },
        },
        inactive: {
          background: UNIT_TOGGLE_BUTTON.INACTIVE.BACKGROUND,
          color: UNIT_TOGGLE_BUTTON.INACTIVE.COLOR,
          hover: {
            background: UNIT_TOGGLE_BUTTON.INACTIVE.HOVER.BACKGROUND,
            color: UNIT_TOGGLE_BUTTON.INACTIVE.HOVER.COLOR,
          },
        },
      },
      mainLayout: {
        background: MAIN_LAYOUT.BACKGROUND,
        primary: {
          main: MAIN_LAYOUT.PRIMARY_COLOR,
          dark: MAIN_LAYOUT.PRIMARY_DARK,
        },
        secondary: {
          main: MAIN_LAYOUT.SECONDARY_COLOR,
        },
        accent: {
          main: MAIN_LAYOUT.ACCENT_COLOR,
        },
        text: {
          primary: MAIN_LAYOUT.TEXT_PRIMARY,
          secondary: MAIN_LAYOUT.TEXT_SECONDARY,
        },
        input: {
          background: MAIN_LAYOUT.INPUT_BACKGROUND,
          border: MAIN_LAYOUT.INPUT_BORDER,
        },
        button: {
          background: MAIN_LAYOUT.BUTTON_BACKGROUND,
          color: MAIN_LAYOUT.BUTTON_COLOR,
          hover: MAIN_LAYOUT.BUTTON_HOVER,
        },
        error: ERROR,
      },
      primary: {
        highlight: '#EFF3FF',
        main: PRIMARY_COLOR,
        dark: PRIMARY_DARK,
      },
      secondary: {
        main: SECONDARY_COLOR,
      },
      text: {
        primary: PRIMARY_TEXT,
        secondary: '#7E7E7E',
        accent: ACCENT_TEXT,
      },
      success: {
        main: SUCCESS,
      },
      warning: {
        main: WARNING,
      },
      error: {
        main: ERROR,
      },
      common: {
        white: colors.common.white,
        black: colors.common.black,
      },
      disabled: {
        warning: '#ffbdad',
      },
      input: {
        background: '#E8EAF6',
        inactive: '#9E9E9E',
      },
      selector: {
        background: '#ffffff',
        hover: '#f5f5f5',
        border: '#333740',
        text: '#333740',
      },
    },
    shape: {
      borderRadius: 10, // default MUI border radius
      xs: 5,
      sm: 5,
      md: 10,
      lg: 15,
      xl: 20,
      xxl: 25,
      round: 50,
    },
    overrides: {
      MuiFormControl: {
        marginDense: {
          marginTop: 20,
        },
      },
      MuiButton: {
        root: {
          borderRadius: 25, // theme.shape.xxl
          minWidth: '170px',
          height: '40px',
        },
        sizeSmall: {
          minWidth: '100px',
          height: '28px',
        },
        contained: {
          backgroundColor: PRIMARY_COLOR,
          color: colors.common.white,
          '&:hover': {
            backgroundColor: PRIMARY_DARK,
          },
        },
        outlined: {
          backgroundColor: colors.common.white,
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: 5, // theme.shape.sm
          backgroundColor: colors.common.white,
        },
      },
      MuiTypography: {
        h2: {
          marginBottom: '1.5rem',
        },
      },
      MuiTableContainer: {
        root: {
          borderRadius: 25, // theme.shape.xxl
          backgroundColor: colors.common.white,
          boxShadow: '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 2px 0 rgb(63 63 68 / 15%)',
        },
      },
      MuiTableRow: {
        head: {
          backgroundColor: PRIMARY_COLOR,
        },
      },
      MuiTableCell: {
        root: {
          flexShrink: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        head: {
          backgroundColor: PRIMARY_COLOR,
          color: ACCENT_TEXT,
          fontSize: 12,
          lineHeight: '1rem',
        },
        stickyHeader: {
          backgroundColor: PRIMARY_COLOR,
          color: ACCENT_TEXT,
          fontSize: 12,
          '&:hover': {
            color: ACCENT_TEXT,
          },
        },
      },
      MuiTableSortLabel: {
        root: {
          '&:hover': {
            color: ACCENT_TEXT,
          },
          '&:focus': {
            color: ACCENT_TEXT,
          },
          '&$active': {
            color: ACCENT_TEXT,
          },
        },
        icon: {
          color: `${ACCENT_TEXT} !important`,
        },
        active: {},
      },
      MuiInputLabel: {
        shrink: {
          backgroundColor: colors.common.white,
        },
      },
      MuiChip: {
        root: {
          backgroundColor: DARK_BACKGROUND,
          color: colors.common.white,
          '&:active': {
            backgroundColor: DARK_BACKGROUND,
          },
        },
        deletable: {
          '&:focus': {
            backgroundColor: DARK_BACKGROUND,
          },
        },
        deleteIcon: {
          color: colors.common.white,
          '&:hover': {
            color: colors.grey[500],
          },
        },
      },
      MuiTooltip: {
        tooltip: {
          fontSize: 15,
        },
      },
    },
    shadows,
    typography,
  });
};

const useTheme = () => {
  const hostname = useMemo(() => window.location.hostname, []);
  const config = useMemo(() => getBrandConfig(hostname), [hostname]);

  const theme = useMemo(() => createCustomTheme(config.theme), [config]);

  useEffect(() => {
    const favicon = document.querySelector('link[rel="shortcut icon"]');
    if (favicon) {
      favicon.href = config.logo.favicon.src;
    }
    document.title = config.title;
  }, [config]);

  return {
    theme,
    logo: config.logo,
    bannersEnabled: config.bannersEnabled,
    companyInfo: config.companyInfo,
    ol1Enabled: config.ol1Enabled,
    poweredByEnabled: config.poweredByEnabled,
    overrideExistingRegistration: config.overrideExistingRegistration,
    viewOnboardingProgress: config.viewOnboardingProgress,
  };
};

export default useTheme;
